/**
 * Façade réseau côté fil principal. Utilise le Web Worker si disponible,
 * sinon le même cœur directement (repli). Numérote les commandes et résout
 * leur promesse à la réception de l'accusé serveur.
 */
import type { AckMsg, Command } from '@snowchain/shared';
import { NetCore, type NetCommand, type NetEvent } from './net-core';

export class NetClient {
  private worker: Worker | null = null;
  private core: NetCore | null = null;
  private seq = 0;
  private pending = new Map<number, { resolve: (a: AckMsg) => void; at: number }>();
  readonly mode: 'worker' | 'main';

  constructor(private readonly onEvent: (e: NetEvent) => void) {
    let worker: Worker | null = null;
    try {
      worker = new Worker(new URL('./net.worker.ts', import.meta.url), { type: 'module', name: 'snowchain-net' });
    } catch {
      worker = null;
    }
    if (worker) {
      this.worker = worker;
      this.mode = 'worker';
      worker.onmessage = (ev: MessageEvent<NetEvent>) => this.dispatch(ev.data);
    } else {
      this.core = new NetCore((e) => this.dispatch(e));
      this.mode = 'main';
    }
  }

  private post(c: NetCommand): void {
    if (this.worker) this.worker.postMessage(c);
    else this.core?.handle(c);
  }

  private dispatch(e: NetEvent): void {
    if (e.k === 'ack') {
      const p = this.pending.get(e.msg.seq);
      if (p) {
        this.pending.delete(e.msg.seq);
        p.resolve(e.msg);
      }
    }
    this.onEvent(e);
  }

  connect(token: string, world?: string): void {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.post({ k: 'connect', url: `${proto}//${location.host}/ws`, token, world });
  }

  /** Envoie une commande ; la promesse se résout avec l'accusé du serveur. */
  send(cmd: Command): Promise<AckMsg> {
    const seq = ++this.seq;
    return new Promise((resolve) => {
      this.pending.set(seq, { resolve, at: performance.now() });
      this.post({ k: 'cmd', seq, cmd });
      // Sans réponse en 15 s : échec local (la commande peut encore arriver).
      setTimeout(() => {
        const p = this.pending.get(seq);
        if (p) {
          this.pending.delete(seq);
          resolve({ t: 'ack', seq, ok: false, error: 'Pas de réponse du serveur' });
        }
      }, 15_000);
    });
  }

  close(): void {
    this.post({ k: 'close' });
  }

  /** Ferme la connexion et libère le worker (partie refusée, retour au menu). */
  dispose(): void {
    this.close();
    for (const [seq, p] of this.pending) p.resolve({ t: 'ack', seq, ok: false, error: 'Déconnecté' });
    this.pending.clear();
    const w = this.worker;
    this.worker = null;
    // Laisse au worker le temps de fermer proprement sa WebSocket.
    if (w) setTimeout(() => w.terminate(), 500);
  }
}
